import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { yen, dateJP } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { Loader2, MessageSquarePlus, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Arrears() {
  const { hasWrite } = useAuth();
  const { nameOf } = useLookups();
  const canWrite = hasWrite("invoices");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ method: "phone", note: "", date: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/billing/arrears"); setRows(data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitReminder = async () => {
    setSaving(true);
    try {
      await api.post(`/billing/arrears/${target.id}/reminder`, form);
      toast.success("督促を記録しました");
      setTarget(null);
      setForm({ method: "phone", note: "", date: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "記録に失敗しました");
    } finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader title="滞納管理" subtitle="家賃未入金の滞納状況と督促履歴" />
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-border rounded-sm py-16 text-center text-muted-foreground">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-gray-300" />現在、滞納はありません
        </div>
      ) : (
        <div className="border border-border bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">入居者</th><th className="px-4 py-3">物件</th>
                  <th className="px-4 py-3">滞納月</th><th className="px-4 py-3">滞納額</th>
                  <th className="px-4 py-3">滞納日数</th><th className="px-4 py-3">督促回数</th>
                  <th className="px-4 py-3">最終連絡</th>{canWrite && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`arrears-row-${i}`}>
                    <td className="px-4 py-3 font-medium">{nameOf("tenants", r.tenant_id)}</td>
                    <td className="px-4 py-3">{nameOf("properties", r.property_id)}</td>
                    <td className="px-4 py-3">{r.year_month}</td>
                    <td className="px-4 py-3 text-red-600 font-medium">{yen(r.arrears_amount)}</td>
                    <td className="px-4 py-3">
                      <span className="bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-sm text-xs">{r.overdue_days}日</span>
                    </td>
                    <td className="px-4 py-3">{(r.reminders || []).length}回</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.last_contact_date ? dateJP(r.last_contact_date) : "-"}</td>
                    {canWrite && (
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => setTarget(r)} data-testid={`reminder-btn-${i}`}>
                          <MessageSquarePlus className="w-3.5 h-3.5 mr-1" />督促記録
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="reminder-dialog">
          <DialogHeader><DialogTitle>督促を記録</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm bg-gray-50 border rounded-sm p-3">
              <div>入居者: <b>{target && nameOf("tenants", target.tenant_id)}</b></div>
              <div>滞納額: <b className="text-red-600">{target && yen(target.arrears_amount)}</b>（{target?.year_month}）</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">連絡方法</Label>
                <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-input bg-white text-sm">
                  <option value="phone">電話</option>
                  <option value="email">メール</option>
                  <option value="letter">書面</option>
                  <option value="visit">訪問</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">連絡日</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">対応内容</Label>
              <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="reminder-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>キャンセル</Button>
            <Button onClick={submitReminder} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="reminder-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}記録
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
